import React, { useState, useEffect } from 'react'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

const CustomLabel = ({ label, description, type, disabled, ...rest }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
        my: 1,
        mx: type === 'no-radio' ? 3 : 0,
        px: 1,
      }}
    >
      {label}
      {description ? (
        <Typography
          variant="caption"
          color={type !== 'no-radio' && !disabled ? 'light-gray-text.main' : 'inherit'}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  )
}

const AdxFormControlLabel = ({
  type,
  control,
  disabled,
  selected,
  label,
  description,
  sx,
  ...rest
}) => {
  // Props
  // 1) type: string
  // accepted value: 'fill', 'no-fill', 'no-radio'
  // 2) selectedvalue: string.
  // props can be passed from the state of the component to check whether the item is selected.

  const [isSelected, setIsSelected] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    if (rest.value === selected) {
      setIsSelected(true)
    } else {
      setIsSelected(false)
    }
  }, [rest.value, selected])

  switch (type) {
    case 'no-fill':
      return (
        <FormControlLabel
          control={control}
          disabled={disabled}
          label={
            <CustomLabel label={label} description={description} type={type} disabled={disabled} />
          }
          {...rest}
          sx={{
            '& .MuiFormControlLabel-label': {
              '&.Mui-disabled': {
                color: 'light-gray-text-2.main',
              },
            },
            pl: 1,
            ...sx,
          }}
        />
      )

    case 'fill':
      return (
        <FormControlLabel
          control={control}
          disabled={disabled}
          label={
            <CustomLabel label={label} description={description} type={type} disabled={disabled} />
          }
          {...rest}
          sx={{
            '&.MuiFormControlLabel-root': {
              backgroundColor: isSelected ? 'primary.light' : 'white',
              borderRadius: '100px',
              mx: 0,
            },
            '& .MuiFormControlLabel-label': {
              color: isSelected ? 'alternate.main' : 'initial',
              width: '100%',
              '&.Mui-disabled': {
                color: 'light-gray-text-2.main',
              },
            },
            pl: 1,
            ...sx,
          }}
        />
      )

    case 'no-radio':
      return (
        <FormControlLabel
          control={control}
          disabled={disabled}
          label={
            <CustomLabel label={label} description={description} type={type} disabled={disabled} />
          }
          {...rest}
          sx={{
            '&.MuiFormControlLabel-root': {
              backgroundColor: isSelected ? 'primary.light' : 'white',
              borderRadius: '100px',
              mx: 0,
              border: isSelected
                ? `1px solid ${theme.palette['alternate'].main}`
                : `1px solid ${theme.palette['gray-line'].main}`,
              '&.Mui-disabled': {
                backgroundColor: 'gray-background.main',
              },
            },
            '& .MuiFormControlLabel-label': {
              color: isSelected ? 'white.main' : 'initial',
              '&.Mui-disabled': {
                color: 'light-gray-text-2.main',
              },
            },
            pl: 1,
            ...sx,
          }}
        />
      )
    case 'outlined':
      return (
        <FormControlLabel
          control={control}
          disabled={disabled}
          label={
            <CustomLabel label={label} description={description} type={type} disabled={disabled} />
          }
          {...rest}
          sx={{
            borderRadius: '100px',
            '&.MuiFormControlLabel-root': {
              backgroundColor: isSelected ? 'primary.light' : 'white',
              mx: 0,
              border: isSelected
                ? `1px solid ${theme.palette['alternate'].main}`
                : `1px solid ${theme.palette['gray-line'].main}`,
              '&.Mui-disabled': {
                backgroundColor: 'gray-background.main',
              },
            },
            '& .MuiFormControlLabel-label': {
              color: isSelected ? 'alternate.main' : 'initial',
              '&.Mui-disabled': {
                color: 'light-gray-text-2.main',
              },
            },
            pl: 1,
            ...sx,
          }}
        />
      )

    default:
      return (
        <FormControlLabel
          control={control}
          disabled={disabled}
          label={
            <CustomLabel label={label} description={description} type={type} disabled={disabled} />
          }
          {...rest}
          sx={{
            '& .MuiFormControlLabel-label': {
              '&.Mui-disabled': {
                color: 'gray-inactive-button.main',
              },
            },
            pl: 1,
            ...sx,
          }}
        />
      )
  }
}

export default AdxFormControlLabel
